// src/pages/tips/ArticleLayout.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ARTICLES } from './TipsIndex';

const AUTHOR = {
  name: 'Michael Morris',
  handle: '@michael_morris',
  bio: 'Home cook, food writer, and the founder of OhAPotato. Michael has spent over a decade writing about practical, everyday cooking — how to cook well on a budget, reduce food waste, and build confidence in the kitchen without fuss.',
  url: 'https://ohapotato.com/about',
};

export default function ArticleLayout({ slug, title, description, emoji, readTime, date, image: Image, children, wordCount }) {
  useEffect(() => {
    document.title = `${title} — OhAPotato`;
    // meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = description;
    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `https://ohapotato.com/tips/${slug}`;
    // Article structured data
    const existingLd = document.getElementById('article-ld');
    if (existingLd) existingLd.remove();
    const ld = document.createElement('script');
    ld.id = 'article-ld';
    ld.type = 'application/ld+json';
    // parse date like "March 2026" → "2026-03-01"
    const monthMap = { January:'01',February:'02',March:'03',April:'04',May:'05',June:'06',July:'07',August:'08',September:'09',October:'10',November:'11',December:'12' };
    const [mon, yr] = (date || '').split(' ');
    const isoDate = (yr && monthMap[mon]) ? `${yr}-${monthMap[mon]}-01` : new Date().toISOString().split('T')[0];
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "image": `https://ohapotato.com/og-image.svg`,
      "datePublished": isoDate,
      "dateModified": isoDate,
      "author": { "@type": "Person", "name": AUTHOR.name, "url": AUTHOR.url },
      "publisher": { "@type": "Organization", "name": "OhAPotato", "url": "https://ohapotato.com", "logo": { "@type": "ImageObject", "url": "https://ohapotato.com/favicon.svg" } },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://ohapotato.com/tips/${slug}` },
      "wordCount": wordCount || 800,
      "articleSection": "Cooking Tips",
      "inLanguage": "en-US"
    });
    document.head.appendChild(ld);
    window.scrollTo(0, 0);
    return () => { const el = document.getElementById('article-ld'); if (el) el.remove(); };
  }, [slug, title, description, date, wordCount]);

  const others = ARTICLES.filter(a => a.slug !== slug).slice(0, 3);

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <Link to="/" style={s.navBrand}>OhAPotato</Link>
        <span style={s.navSep}>›</span>
        <Link to="/tips" style={s.navBack}>Cooking Tips</Link>
        <span style={s.navSep}>›</span>
        <span style={s.navCurrent}>{title}</span>
      </nav>

      {/* Hero image */}
      {Image && (
        <div style={s.heroImgWrap}>
          <Image />
        </div>
      )}

      <main style={s.main}>
        <header style={s.hero}>
          <div style={s.heroEmoji}>{emoji}</div>
          <p style={s.meta}>{readTime} · {date}</p>
          <h1 style={s.title}>{title}</h1>
          <p style={s.desc}>{description}</p>

          {/* Author byline */}
          <div style={s.authorRow}>
            <div style={s.authorAvatar}>
              {AUTHOR.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <p style={s.authorName}>{AUTHOR.name}</p>
              <p style={s.authorHandle}>Food writer · OhAPotato</p>
            </div>
          </div>
        </header>

        <article style={s.article}>
          {children}
        </article>

        {/* Author bio box */}
        <div style={s.authorBox}>
          <div style={s.authorBoxAvatar}>
            {AUTHOR.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p style={s.authorBoxName}>Written by {AUTHOR.name}</p>
            <p style={s.authorBoxBio}>{AUTHOR.bio}</p>
          </div>
        </div>

        <section style={s.more}>
          <h2 style={s.moreTitle}>More cooking guides</h2>
          <div style={s.moreGrid}>
            {others.map(a => (
              <Link to={`/tips/${a.slug}`} key={a.slug} style={s.moreCard}
                onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent-orange)'}
                onMouseLeave={e => e.currentTarget.style.borderColor='var(--border-base)'}
              >
                <span style={s.moreEmoji}>{a.emoji}</span>
                <div>
                  <p style={s.moreCardTitle}>{a.title}</p>
                  <p style={s.moreCardMeta}>{a.readTime}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div style={s.cta}>
          <p style={s.ctaText}>Ready to cook something great?</p>
          <Link to="/" style={s.ctaBtn}>Find recipes from your ingredients →</Link>
        </div>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/tips" style={s.footerLink}>Cooking Tips</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/about" style={s.footerLink}>About</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-primary)' },
  nav: { padding:'14px 32px', borderBottom:'1px solid var(--border-base)', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' },
  navBrand: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--accent-orange)', textDecoration:'none', fontWeight:'700' },
  navBack: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--accent-orange)', textDecoration:'none', fontWeight:'600' },
  navSep: { fontSize:'12px', color:'var(--text-muted)', opacity:0.6 },
  navCurrent: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'220px' },
  heroImgWrap: { width:'100%', maxHeight:'340px', overflow:'hidden', background:'var(--bg-pill)' },
  main: { maxWidth:'680px', margin:'0 auto', padding:'40px 24px 80px' },
  hero: { marginBottom:'36px', paddingBottom:'28px', borderBottom:'1px solid var(--border-base)' },
  heroEmoji: { fontSize:'44px', marginBottom:'14px', lineHeight:1 },
  meta: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'1px', fontWeight:'600' },
  title: { fontFamily:'var(--font-display)', fontSize:'clamp(26px,4vw,36px)', fontWeight:'700', lineHeight:'1.2', letterSpacing:'-0.4px', marginBottom:'12px' },
  desc: { fontFamily:'var(--font-body)', fontSize:'17px', color:'var(--text-secondary)', lineHeight:'1.7', marginBottom:'20px' },
  // Author byline
  authorRow: { display:'flex', alignItems:'center', gap:'10px', marginTop:'4px' },
  authorAvatar: {
    width:'36px', height:'36px', borderRadius:'50%',
    background:'var(--accent-orange)', color:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontFamily:'var(--font-display)', fontSize:'13px', fontWeight:'700', flexShrink:0,
  },
  authorName: { fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:'600', color:'var(--text-primary)', margin:0 },
  authorHandle: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)', margin:0 },
  // Article body
  article: { fontFamily:'var(--font-body)', fontSize:'16px', color:'var(--text-secondary)', lineHeight:'1.8', marginBottom:'48px' },
  // Author bio box
  authorBox: {
    display:'flex', gap:'14px', alignItems:'flex-start',
    background:'var(--bg-card)', border:'1px solid var(--border-base)',
    borderRadius:'var(--radius-lg)', padding:'20px 22px',
    marginBottom:'48px',
  },
  authorBoxAvatar: {
    width:'44px', height:'44px', borderRadius:'50%', flexShrink:0,
    background:'var(--accent-orange)', color:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontFamily:'var(--font-display)', fontSize:'15px', fontWeight:'700',
  },
  authorBoxName: { fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'700', color:'var(--text-primary)', margin:'0 0 4px' },
  authorBoxBio: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-secondary)', lineHeight:'1.6', margin:0 },
  // More articles
  more: { marginBottom:'48px', paddingTop:'40px', borderTop:'1px solid var(--border-base)' },
  moreTitle: { fontFamily:'var(--font-display)', fontSize:'20px', fontWeight:'600', marginBottom:'16px', color:'var(--text-primary)' },
  moreGrid: { display:'flex', flexDirection:'column', gap:'10px' },
  moreCard: { display:'flex', gap:'14px', alignItems:'center', padding:'14px 16px', background:'var(--bg-card)', border:'1px solid var(--border-base)', borderRadius:'var(--radius-md)', textDecoration:'none', color:'inherit', transition:'border-color 0.14s ease' },
  moreEmoji: { fontSize:'24px', flexShrink:0 },
  moreCardTitle: { fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'600', color:'var(--text-primary)', marginBottom:'3px', lineHeight:'1.3' },
  moreCardMeta: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)' },
  // CTA
  cta: { background:'linear-gradient(135deg, var(--accent-orange-bg), var(--accent-gold-bg))', border:'1px solid var(--accent-orange)', borderRadius:'var(--radius-lg)', padding:'28px 32px', textAlign:'center', marginBottom:'48px' },
  ctaText: { fontFamily:'var(--font-display)', fontSize:'20px', fontWeight:'600', color:'var(--text-primary)', marginBottom:'14px' },
  ctaBtn: { display:'inline-block', padding:'11px 24px', background:'var(--accent-orange)', color:'#fff', borderRadius:'var(--radius-pill)', fontFamily:'var(--font-body)', fontSize:'14px', fontWeight:'600', textDecoration:'none' },
  footer: { paddingTop:'24px', borderTop:'1px solid var(--border-base)', display:'flex', gap:'12px', alignItems:'center', justifyContent:'center' },
  footerLink: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)', textDecoration:'none' },
  footerDot: { color:'var(--text-muted)', fontSize:'11px' },
};
